import React, { useEffect, useState } from "react";
import { API_BASE } from "../../config";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const CasesTable = ({ cases = [], typeName }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const itemsPerPage = 10;

  const safeCases = Array.isArray(cases) ? cases : [];
  const totalPages = Math.max(1, Math.ceil(safeCases.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCases = safeCases.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseModal();
      }
    };

    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModal]);

  const handleCaseClick = (caseData) => {
    setSelectedCase(caseData);
    setShowModal(true);
  };

  const handleEscalate = async (caseId) => {
    if (caseId == null) return;

    console.log("Escalate case " + caseId);

    let removeLoading;

    try {
      removeLoading = showLoading();

      const res = fetchWithAuth(
        `${API_BASE}/ainstein/in/escalate-case?case_id=${encodeURIComponent(
          caseId
        )}`,
        { method: "GET" }
      );

      await res;
      window.location.reload(false);
    } catch (err) {
      removeLoading?.();
      console.error("Escalate error", err);
    }
  };

  const handleReset = async (caseId) => {
    if (caseId == null) return;

    console.log("Reset case " + caseId);

    let removeLoading;

    try {
      removeLoading = showLoading();

      const res = fetchWithAuth(
        `${API_BASE}/ainstein/in/reset-case?case_id=${encodeURIComponent(
          caseId
        )}`,
        { method: "GET" }
      );

      await res;
      window.location.reload(false);
    } catch (err) {
      removeLoading?.();
      console.error("Reset error", err);
    }
  };

  const showLoading = () => {
    const overlay = document.createElement("div");

    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100%";
    overlay.style.height = "100%";
    overlay.style.backgroundColor = "#50505044";
    overlay.style.zIndex = "9999";
    overlay.style.display = "flex";
    overlay.style.justifyContent = "center";
    overlay.style.alignItems = "center";
    overlay.innerHTML =
      '<div class="spinner-border text-light" role="status"><span class="sr-only"></span></div>';

    document.body.appendChild(overlay);

    return () => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
    };
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCase(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      return date.toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch (e) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "OPEN":
        return "#007bff";
      case "IN PROGRESS":
      case "IN_PROGRESS":
        return "#ffc107";
      case "CLOSED":
      case "RESOLVED":
        return "#28a745";
      default:
        return "#6c757d";
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (currentPage > 1) {
      pages.push(
        <button
          key="prev"
          onClick={() => setCurrentPage(currentPage - 1)}
          className="pagination-btn"
          type="button"
        >
          ←
        </button>
      );
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => setCurrentPage(1)}
          className="pagination-btn"
          type="button"
        >
          1
        </button>
      );

      if (startPage > 2) {
        pages.push(
          <span key="ellipsis1" className="pagination-ellipsis">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`pagination-btn ${i === currentPage ? "active" : ""}`}
          type="button"
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="ellipsis2" className="pagination-ellipsis">
            ...
          </span>
        );
      }

      pages.push(
        <button
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
          className="pagination-btn"
          type="button"
        >
          {totalPages}
        </button>
      );
    }

    if (currentPage < totalPages) {
      pages.push(
        <button
          key="next"
          onClick={() => setCurrentPage(currentPage + 1)}
          className="pagination-btn"
          type="button"
        >
          →
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="cases-table-container">
      <div className="cases-table-header">
        <h4>
          {typeName} Cases ({safeCases.length.toLocaleString()})
        </h4>

        <div className="pagination-info">
          Page {currentPage} of {totalPages}
        </div>
      </div>

      <table className="cases-table">
        <thead>
          <tr>
            <th>Case ID</th>
            <th>Last Update</th>
            <th>Current Step</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {currentCases.map((caseData) => (
            <tr key={caseData.CASE_ID} className="case-row">
              <td>{caseData.CASE_ID}</td>
              <td>{formatDate(caseData.LAST_UPDATE)}</td>
              <td>{caseData.CURRENT_STEP}</td>
              <td>
                <span
                  className="status-badge"
                  style={{
                    backgroundColor: getStatusColor(caseData.STATUS),
                  }}
                >
                  {caseData.STATUS}
                </span>
              </td>
              <td>
                <button
                  onClick={() => handleCaseClick(caseData)}
                  className="info-btn"
                  title="View Notes"
                  type="button"
                >
                  Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 1 && <div className="pagination">{renderPagination()}</div>}

      {showModal && (
        <div
          className="remediation-modal-backdrop"
          onClick={handleCloseModal}
          role="presentation"
        >
          <div
            className="remediation-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remediation-case-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="remediation-modal-header">
              <h3
                id="remediation-case-modal-title"
                className="remediation-modal-title"
              >
                Case {selectedCase?.CASE_ID} - Notes
              </h3>

              <div className="remediation-modal-actions">
                <button
                  className="remediation-modal-action-button remediation-modal-action-button--warning"
                  onClick={() => handleEscalate(selectedCase?.CASE_ID)}
                  disabled={!selectedCase}
                  title="Escalate this case"
                  type="button"
                >
                  Escalate case
                </button>

                <button
                  className="remediation-modal-action-button remediation-modal-action-button--danger"
                  onClick={() => handleReset(selectedCase?.CASE_ID)}
                  disabled={!selectedCase}
                  title="Reset this case"
                  type="button"
                >
                  Reset case
                </button>

                <button
                  className="remediation-modal-close"
                  onClick={handleCloseModal}
                  type="button"
                  aria-label="Close case notes"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="remediation-modal-body">
              {selectedCase && (
                <div className="case-notes">
                  <div className="case-info">
                    <p>
                      <strong>Case ID:</strong> {selectedCase.CASE_ID}
                    </p>

                    <p>
                      <strong>Current Step:</strong>{" "}
                      {selectedCase.CURRENT_STEP}
                    </p>

                    <p>
                      <strong>Status:</strong>
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor: getStatusColor(selectedCase.STATUS),
                          marginLeft: "8px",
                        }}
                      >
                        {selectedCase.STATUS}
                      </span>
                    </p>

                    <p>
                      <strong>Last Update:</strong>{" "}
                      {formatDate(selectedCase.LAST_UPDATE)}
                    </p>
                  </div>

                  <div className="notes-content">
                    <h5>Notes:</h5>

                    <div className="notes-text">
                      {String(selectedCase.NOTES || "No notes available.")
                        .split("\n")
                        .map((note, index) => (
                          <div key={index} className="note-entry">
                            {note}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CasesTable;